import axios from "axios";
import {
  Recipe,
  UserLogin,
  UserSignUp,
  Book,
  AttributeData,
  Instruction,
  Recipes,
} from "./utils/types";
import { errorHandling } from "./utils/ErrorHandling";

export const BASEURL = process.env.REACT_APP_BASE_URL;

export const protocol =
  process.env.NODE_ENV === "development" ? "http" : "https";

/** API class.
 * Static class - Contains methods that facilitate communications between client
 * and backend API */
class API {
  static token;

  static async request(
    endpoint: string,
    data: object = {},
    method: string = "GET",
  ) {
    const url = `${protocol}://${BASEURL}/${endpoint}`;
    const headers = {
      Authorization: `Bearer ${this.token}`,
    };
    const params = method === "GET" ? data : {};
    try {
      const res = (await axios({ url, method, data, params, headers })).data;
      return res;
    } catch (error) {
      console.error(`Error in ${endpoint} - API => ${error}`);
      throw error;
    }
  }

  // ########### USERS ###########
  // ################################

  /** Register user: returns token */
  static async signUp(data: UserSignUp, token?: string) {
    API.token = token;
    const res = await this.request("signup", data, "POST");
    return res;
  }

  /**Authenticate user: returns token */
  static async login(data: UserLogin) {
    const res = await this.request("login", data, "POST");
    return res;
  }

  /** Fetch specific user */
  static async getUser(userId: number) {
    const res = await this.request(`users/${userId}`);
    return res;
  }

  /** Request reset link to be sent to user*/
  static async postResetRequest(email: string) {
    const res = await this.request(`initiate_reset/${email}`, {}, "POST");
    return res;
  }

  /** Reset User password */
  static async postPasswordReset(
    password: string,
    userName: string,
    token: string,
  ) {
    API.token = token;
    const res = await this.request(
      `request_reset`,
      { password, userName },
      "POST",
    );
    return res;
  }

  static async inviteTester(email: string) {
    const res = await this.request("invite", { email }, "POST");
    return res;
  }

  static async requestInvite(email: string) {
    const res = await this.request("request_invite", { email }, "POST");
    return res;
  }

  // ############ RECIPES ###########
  // ################################
  /** Create new default book and copy recipe to new default book */
  static async postCreateBookCopyRecipe(
    book: Book,
    recipe: Recipe,
  ): Promise<{ book: Book; recipes: Recipes }> {
    const res = await this.request(
      `create_copy`,
      { title: book.title, description: book.description, recipe: recipe },
      "POST",
    );
    return res;
  }

  /** Add user recipe to corresponding book*/
  static async postUserRecipe(recipe: Recipe, bookId: number, userId: number) {
    const res = await this.request(
      `users/${userId}/books/${bookId}/recipes`,
      recipe,
      "POST",
    );
    return res;
  }

  /** Fetch user recipes */
  static async getBookRecipes(userId: number, bookId: number) {
    const res = await this.request(`users/${userId}/books/${bookId}/recipes`);
    return res;
  }

  /** Edit user recipe */
  static async patchUserRecipe(bookId: number, recipeId: number, data: any) {
    const res = await this.request(
      `books/${bookId}/recipes/${recipeId}`,
      data,
      "PATCH",
    );
    return res;
  }

  /** Delete user recipe */
  static async deleteUserRecipe(
    userId: number,
    bookId: number,
    recipeId: number,
    createdById: number,
  ) {
    const res = await this.request(
      `users/${userId}/books/${bookId}/recipes/${recipeId}`,
      { createdById },
      "DELETE",
    );
    return res;
  }

  /** Share recipe with another user */
  static async postShareRecipe(recipeId: number, recipient: string) {
    const res = await this.request(
      `share_recipes/${recipeId}`,
      { recipient },
      "POST",
    );
    return res;
  }

  /** Delete shared recipe association */
  static async deleteSharedRecipe(bookId: number, recipeId: number) {
    const res = await this.request(
      `books/${bookId}/share_recipes/${recipeId}`,
      {},
      "DELETE",
    );
    return res;
  }

  /** Copy & change ownership of user's recipe to recipient recipe book */
  static async postCopySharedRecipe(bookId: number, recipe: Recipe) {
    const recipeId = recipe.id;
    const res = await this.request(
      `books/${bookId}/recipes/${recipeId}`,
      recipe,
      "POST",
    );
    return res;
  }

  // ########### BOOKS ###########
  // ################################

  /** Post new book */
  static async postBook(data: Book, userId: number) {
    const res = await this.request(`users/${userId}/books`, data, "POST");
    return res;
  }

  /** Fetch user books */
  static async getUserBooks(userId: number) {
    const res = await this.request(`users/${userId}/books`);
    return res;
  }

  /** Post shared book id - create association */
  static async postShareBook(userId: number, bookId: number, data: object) {
    const res = await this.request(
      `users/${userId}/books/${bookId}`,
      data,
      "POST",
    );
    return res;
  }

  // ########### COMPONENT OPTIONS = {amount, unit, item} = INGREDIENT ###########
  // ################################################################

  /** Fetch all ingredients  */
  static async getUserIngredientOptions(userId: number) {
    const res = await this.request(`/users/${userId}/ingredients/components`);
    return res;
  }

  /** Fetch book ingredients */
  static async getBookIngredientOptions(userId: number, bookId: number) {
    const res = await this.request(
      `/users/${userId}/books/${bookId}/ingredients/components`,
    );
    return res;
  }

  /** Add ingredient - NOT IN USE*/
  static async postIngredient(data: AttributeData, option: string) {
    const res = await this.request(`ingredients/${option}`, data, "POST");
    return res;
  }

  /** Add book ingredient */
  static async postIngredientOption(
    option: AttributeData,
    bookId: number,
    userId: number,
    component: string,
  ) {
    const res = await this.request(
      `/users/${userId}/books/${bookId}/ingredients/${component}`,
      option,
      "POST",
    );
    return res;
  }

  /** Post option association */
  static async postOptionAssociation(
    userId: number,
    bookId: number,
    optionId: number,
    component: string,
  ) {
    const res = await this.request(
      `/users/${userId}/books/${bookId}/components/${component}/options/${optionId}`,
      {},
      "POST",
    );
    return res;
  }

  // ########### INSTRUCTIONS ###########
  // ################################

  /** Fetch all instructions */
  static async getInstructions() {
    const res = await this.request("instructions");
    return res;
  }

  /** Fetch user instructions */
  static async getUserInstructions(userId: number) {
    const res = await this.request(`/users/${userId}/instructions`);
    return res;
  }

  /** Fetch book instructions */
  static async getBookInstructions(userId: number, bookId: number) {
    const res = await this.request(
      `/users/${userId}/books/${bookId}/instructions`,
    );
    return res;
  }

  /** Post instruction to database */
  static async postInstruction(
    userId: number,
    bookId: number,
    data: Instruction,
  ) {
    const res = await this.request(
      `users/${userId}/books/${bookId}/instructions`,
      data,
      "POST",
    );
    return res;
  }

  /** Post instruction association */
  static async postInstructionAssociation(
    userId: number,
    bookId: number,
    instructionId: number,
  ) {
    const res = await this.request(
      `users/${userId}/books/${bookId}/instructions/${instructionId}`,
      {},
      "POST",
    );
    return res;
  }

  // ########### DATA - User or Book  ###########
  // ################################
  static async getUserData(){
    return await this.request("users/ingredients/instructions")
  }

  static async getBookData(bookId: number){
  return await this.request(`books/${bookId}/ingredients/instructions`)
  }

}

export default API;
